import type { InputHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from './cn';

interface FieldShellProps {
  label?: string;
  error?: string | undefined;
  hint?: string;
  children: (a: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

export function FieldShell({ label, error, hint, children, className }: FieldShellProps) {
  const id = useId();
  const msgId = `${id}-msg`;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-ink-2">
          {label}
        </label>
      )}
      {children({ id, describedBy: error || hint ? msgId : undefined, invalid: Boolean(error) })}
      {(error || hint) && (
        <p id={msgId} role={error ? 'alert' : undefined} className={cn('text-xs', error ? 'font-medium text-danger' : 'text-ink-2')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

const control =
  'w-full rounded-md border bg-fill px-4 py-3 text-[15px] text-ink placeholder:text-ink-3 transition ' +
  'hover:border-turquoise/40 focus:border-turquoise focus:bg-paper focus:outline-none focus:ring-4 focus:ring-turquoise/15 disabled:opacity-60';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
  hint?: string;
  ref?: Ref<HTMLInputElement>;
  containerClassName?: string;
}

export function Input({ label, error, hint, className, containerClassName, ref, ...rest }: InputProps) {
  return (
    <FieldShell label={label} error={error} hint={hint} className={containerClassName}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          ref={ref}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(control, invalid ? 'border-danger' : 'border-line', className)}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | undefined;
  ref?: Ref<HTMLTextAreaElement>;
}
export function Textarea({ label, error, className, ref, ...rest }: TextareaProps) {
  return (
    <FieldShell label={label} error={error}>
      {({ id, describedBy, invalid }) => (
        <textarea id={id} ref={ref} aria-invalid={invalid || undefined} aria-describedby={describedBy}
          className={cn(control, 'min-h-24 resize-y', invalid ? 'border-danger' : 'border-line', className)} {...rest} />
      )}
    </FieldShell>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | undefined;
  ref?: Ref<HTMLSelectElement>;
}
export function Select({ label, error, className, children, ref, ...rest }: SelectProps) {
  return (
    <FieldShell label={label} error={error}>
      {({ id, describedBy, invalid }) => (
        <select id={id} ref={ref} aria-invalid={invalid || undefined} aria-describedby={describedBy}
          className={cn(control, invalid ? 'border-danger' : 'border-line', className)} {...rest}>
          {children}
        </select>
      )}
    </FieldShell>
  );
}
